import { gql } from '@apollo/client/core';
import { executeQuery, executeMutation } from '@/components/commons/utils';
import { computed, ref, reactive } from 'vue';
import Tracking from '@/components/commons/Tracking';
import ColourMapping from '@/components/commons/ColourUtils.js';
import LocationService from '@/services/LocationService';
import { formatMoney } from '@/components/cart/utils';
import CountryUtils from '@/components/commons/CountryUtils';
import { useRouter } from 'vue-router';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';

export const SEARCH_PRODUCTS_BY_IDS = gql`
  query SEARCH_PRODUCTS($productIds: [ID]) {
    searchProductsByIds(productIds: $productIds) {
      products {
        title
        hashKey
        isEtaAtRisk
        imageUrl
        navigationCategory {
          name
          id
        }
      }
    }
  }
`;

export const SEARCH_PRODUCTS_QUERY = gql`
  query SEARCH_PRODUCTS(
    $navigationCategories: [ID]
    $taxonomyCategories: [ID]
    $taxonomyAttributes: [ID]
    $excludedProducts: [ID]
    $onSale: Boolean
    $onClearance: Boolean
    $inStock: Boolean
    $hasReasonableETA: Boolean
    $landingPageId: Int
    $collectionId: Int
    $sort: ProductSortType
    $query: String
    $page: Int
    $pageSize: Int
    $preSelectedTaxonomyAttributes: [ID]
    $countryCode: String
  ) {
    searchProducts(
      navigationCategories: $navigationCategories
      taxonomyCategories: $taxonomyCategories
      taxonomyAttributes: $taxonomyAttributes
      excludedProducts: $excludedProducts
      onSale: $onSale
      onClearance: $onClearance
      inStock: $inStock
      landingPageId: $landingPageId
      collectionId: $collectionId
      hasReasonableETA: $hasReasonableETA
      sort: $sort
      query: $query
      page: $page
      pageSize: $pageSize
      preSelectedTaxonomyAttributes: $preSelectedTaxonomyAttributes
      countryCode: $countryCode
    ) {
      products {
        hashKey
        baseHashKey
        title
        skuNo
        slug
        imageUrl
        onSale
        onClearance
        isNewProduct
        settingImageUrl
        variation {
          hashKey
          fabricAndStain
          popularity
          imageUrl
          variationImageUrl
          title
          slug
          onSale
          onClearance
          isNewProduct
          settingImageUrl

          priceMap {
            USD {
              currencyCode
              price
              retail
              sale
              clearance
            }
            CAD {
              currencyCode
              price
              retail
              sale
              clearance
            }
          }
        }
        priceMap {
          USD {
            currencyCode
            price
            retail
            sale
            clearance
          }
          CAD {
            currencyCode
            price
            retail
            sale
            clearance
          }
        }
      }
      count
      maxPage
      page
      taxonomyCategories {
        id
        name
        count
        disabled
      }
      taxonomyAttributes {
        id
        name
        count
        attributes {
          id
          attributeId
          name
          valueId
          count
          disabled
        }
      }
      onSale {
        name
        count
      }
      onClearance {
        name
        count
      }
      inStock {
        name
        count
      }
      queryAfterRemoval
      queryID
      redirectUrl {
        url
        title
      }
    }
  }
`;

export const TAXONOMY_MAP_QUERY = gql`
  query TAXONOMY_CATEGORY {
    productTaxonomyCategoryAttributeMap {
      categoryMap
      attributeMap
      attributeValueMap
    }
  }
`;

export const COMMON_CATEGORY_QUERY = gql`
  query COMMON_CATEGORIES {
    commonData {
      categories {
        id
        name
        displayName
        url
        parent {
          id
        }
        parentId
        path
      }
      maxClearanceDiscountCA
      maxClearanceDiscountUS
    }
  }
`;

export const PRODUCT_SUGGESTION_QUERY = gql`
  query PRODUCT_SUGGESTION($suggestionQuery: String, $suggestionCount: Int) {
    searchProductSuggestion(query: $suggestionQuery, suggestionCount: $suggestionCount) {
      query
      highlightedQuery
    }
  }
`;

export const SEND_PRODUCT_SEARCH_RESULT_MUTATION = gql`
  mutation SEND_PRODUCT_RESULT_EVENT($input: SendSearchResultEventInput!) {
    sendProductSearchResultEvent(input: $input) {
      success
    }
  }
`;

const SORT_OPTIONS = [
  { id: 'POPULARITY', description: 'Popularity', queryName: 'popularity' },
  { id: 'PRICE_HIGH_LOW', description: 'High - Low Price', queryName: 'price-high' },
  { id: 'PRICE_LOW_HIGH', description: 'Low - High Price', queryName: 'price-low' },
  { id: 'NEWEST', description: 'Newest', queryName: 'newest' },
];

const searchProductsRef = ref({ products: [], redirectUrl: {} });
const filters = reactive({
  navigationCategories: [],
  taxonomyCategories: [],
  taxonomyAttributes: [],
  excludedProducts: [],
  onSale: undefined,
  onClearance: undefined,
  inStock: undefined,
  landingPageId: undefined,
  hasReasonableETA: undefined,
  collectionId: undefined,
  sort: 'POPULARITY',
  query: '',
  page: 0,
  pageSize: 20,
});
const preSelectedFilters = ref({});
const preSelectedTaxonomyAttributes = ref([]);
const shouldForceSaleFilter = ref(false);
const taxonomyMap = ref({});
const navCategories = ref([]);
const pending = ref(false);
const seoPage = ref(null);

export function formatProductPrice(product) {
  const currencyCode = LocationService.isCanada() ? 'CAD' : 'USD';

  if (product === undefined) {
    product = {};
  }

  if (!product.formatted) {
    product.imageUrl = `${product.imageUrl}?fit=max&w={width}&q={q}`;
    product.variationImageUrl = `${product.variationImageUrl}?fit=max&w={width}&q={q}`;
    product.settingImageUrl = `${product.settingImageUrl}?fit=max&w={width}&q={q}`;

    let priceKey = 'price';
    if (product.onClearance) {
      priceKey = 'clearance';
    } else if (product.onSale) {
      priceKey = 'sale';
    }

    let { priceMap } = product;
    if (priceMap) {
      priceMap = priceMap[currencyCode];
      product.originalPrice = formatMoney(CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol, priceMap.price);
      product.priceCents = priceMap[priceKey] || priceMap.price;
      product.price = formatMoney(CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol, product.priceCents);
    }
    product.formatted = true;
  }
}

const products = computed(() => {
  const newProducts = [...(searchProductsRef.value.products || [])];
  newProducts?.forEach((product) => {
    formatProductPrice(product);

    const variations = product?.variation || product?.variations || [];
    variations.forEach(formatProductPrice);
  });

  return newProducts;
});

const maxPage = computed(() => searchProductsRef.value.maxPage || 0);

const taxonomyCategories = computed(() => searchProductsRef.value.taxonomyCategories || []);

const taxonomyAttributes = computed(() => {
  const colorAttribute = searchProductsRef.value.taxonomyAttributes?.find((attribute) => attribute.name === 'Color');
  if (colorAttribute) {
    colorAttribute.attributes.forEach((value) => {
      value.hex = ColourMapping[value.name];
    });
  }

  return searchProductsRef.value.taxonomyAttributes || [];
});

const productsCount = computed(() => searchProductsRef.value.count);

const inStockEnabled = computed(() => !!searchProductsRef.value.inStock?.find((stock) => stock.name === 'true')?.count ?? false);

const saleCount = computed(() => searchProductsRef.value.onSale?.find((sale) => sale.name === 'true')?.count ?? 0);

const clearanceCount = computed(() => searchProductsRef.value.onClearance?.find((clearance) => clearance.name === 'true')?.count ?? 0);

const isNoProducts = computed(() => !products.value?.length);

const queryAfterRemoval = computed(() => searchProductsRef.value.queryAfterRemoval || '');

const queryID = computed(() => searchProductsRef.value.queryID || '');

const redirectUrl = ref({});

const maxClearanceDiscountCA = ref(0);
const maxClearanceDiscountUS = ref(0);

function loadCurrentRouteFilters(vueRouter) {
  const router = vueRouter || useRouter();
  //  100 is the ID for the 'New' browse page // 195 is for the outdoor promotion page
  const sortBrowsePageByNew = router.currentRoute.value.params.id === '100' || router.currentRoute.value.params.id === '195';

  if (sortBrowsePageByNew) {
    filters.sort = 'NEWEST';
  } else {
    filters.sort = 'POPULARITY';
  }

  const query = router.currentRoute.value.query ?? {};

  if (Object.keys(query).length) {
    const sort = SORT_OPTIONS.find((option) => option.queryName === query.sort);

    filters.taxonomyCategories = query.tc?.split(',') ?? [];
    filters.taxonomyAttributes = query.av?.split(',') ?? [];
    filters.sort = sort?.id ?? sortBrowsePageByNew ? 'NEWEST' : 'POPULARITY';
    filters.inStock = query.stock === 'true' ? true : filters.inStock;
    filters.hasReasonableETA = query.hasReasonableETA === 'true' ? true : filters.hasReasonableETA;
    filters.onSale = query.sale === 'true' ? true : filters.onSale;
    filters.onClearance = query.clearance === 'true' ? true : filters.onClearance;
  }
}

/**
 * Track a product added to the cart from search results.
 *
 * @param {Number} productId The id of the product that was added to the cart
 */
export async function trackProductAddToCart(productId) {
  try {
    const productClicked = JSON.parse(LocalStorage.get(LocalStorageKeys.SEARCH_PRODUCT_CLICK));
    if (productId && productClicked && productClicked.productId && parseInt(productClicked.productId, 10) === productId) {
      await executeMutation(SEND_PRODUCT_SEARCH_RESULT_MUTATION, {
        input: {
          eventType: 'ADD_TO_CART',
          queryID: productClicked.queryId,
          objectID: productId.toString(),
        },
      });
    }
    LocalStorage.set(LocalStorageKeys.SEARCH_PRODUCT_CLICK, null);
  } catch (e) {
    console.error(e);
  }
}

export default function useFilterProduct() {
  const router = useRouter();

  const isPromotionPage = computed(() => {
    const currentRouteName = router.currentRoute.value?.name ?? null;
    return currentRouteName === 'promotion' || currentRouteName === 'currentPromotion';
  });

  const filterCount = computed(() => {
    const allFilters = [...filters.taxonomyCategories, ...filters.taxonomyAttributes, filters.onClearance, filters.inStock];

    !isPromotionPage.value && allFilters.push(filters.onSale);

    return allFilters.filter(Boolean).length;
  });

  async function loadBrowseProducts({ replace = true } = {}) {
    await getBrowseProducts(filters, replace);
  }

  function setHasReasonableETAFilter(value) {
    filters.hasReasonableETA = value;
  }

  async function fetchProductsForContentfulProductFeed(categoryId, taxonomyIdList, taxonomyAttributes, sortOption, nonGlobaltaxonomyAttributes) {
    let products = [];
    try {
      const response = await fetchProductsWithFilters({
        navigationCategories: categoryId,
        taxonomyCategories: taxonomyIdList,
        taxonomyAttributes: taxonomyAttributes,
        inStock: true,
        sort: sortOption,
        pageSize: 30,
        nonGlobaltaxonomyAttributes,
      });
      products = response?.products ?? [];
      return products.map((product) => {
        product.id = product.hashKey;
        (product.isOnSale = product.onSale),
          (product.isOnClearance = product.onClearance),
          (product.productImage = {
            url: `${product.imageUrl}?fit=max&w={width}&q={q}`,
          });
        product.url = router.resolve({ name: 'product', params: { id: product.hashKey, slug: product.slug } }).href;
        formatProductPrice(product);
        product.variation.map((variation) => formatProductPrice(variation));
        return product;
      });
    } catch (e) {
      Tracking.sentryCaptureError(e);
      return [];
    }
  }

  async function getBrowseProducts(
    {
      navigationCategories, //
      taxonomyCategories,
      taxonomyAttributes,
      excludedProducts,
      onSale,
      onClearance,
      inStock,
      landingPageId,
      hasReasonableETA,
      collectionId,
      sort,
      query,
      page,
      pageSize,
    } = {},
    replace = true,
  ) {
    pending.value = true;

    try {
      const searchProducts = await fetchProductsWithFilters({
        navigationCategories, //
        taxonomyCategories,
        taxonomyAttributes,
        excludedProducts,
        onSale,
        onClearance,
        hasReasonableETA,
        inStock,
        landingPageId,
        collectionId,
        sort,
        query,
        page,
        pageSize,
      });
      if (searchProducts) {
        if (replace) {
          searchProductsRef.value = searchProducts;
        } else {
          let indexPosition = parseInt(searchProducts.page * filters.pageSize);

          searchProductsRef.value.products.length = searchProducts.count;

          searchProductsRef.value.products.splice(indexPosition, filters.pageSize, ...searchProducts.products);
        }
        pending.value = false;
        return;
      }

      throw `getBrowseProducts - Products fetch didn't return a value`;
    } catch (error) {
      console.error(error);
      Tracking.sentryCaptureError(error);
    }
    pending.value = false;
  }

  async function fetchProductsWithFilters({
    navigationCategories, //
    taxonomyCategories,
    taxonomyAttributes,
    excludedProducts,
    onSale,
    onClearance,
    inStock,
    landingPageId,
    hasReasonableETA,
    collectionId,
    sort,
    query,
    page,
    pageSize,
    nonGlobaltaxonomyAttributes,
  } = {}) {
    // onSale and onClearance filters should be either undefined or true.
    // Setting them to false will cause Algolia to filter out to only products
    // that are not on sale/clearance.
    let isOnSale = !onSale ? undefined : true;
    let isOnClearance = !onClearance ? undefined : true;

    if (shouldForceSaleFilter.value && !isOnSale && !isOnClearance) {
      isOnSale = true;
      isOnClearance = true;
    }

    try {
      const { searchProducts } = await executeQuery(SEARCH_PRODUCTS_QUERY, {
        ...{
          navigationCategories,
          taxonomyCategories,
          taxonomyAttributes,
          excludedProducts,
          onSale: isOnSale,
          onClearance: isOnClearance,
          hasReasonableETA,
          inStock,
          landingPageId,
          collectionId,
          sort,
          query,
          page,
          pageSize,
          countryCode: LocationService.getCurrentCountryCode(),
        },
        ...preSelectedFilters.value,
        preSelectedTaxonomyAttributes:
          nonGlobaltaxonomyAttributes && nonGlobaltaxonomyAttributes.length ? nonGlobaltaxonomyAttributes : preSelectedTaxonomyAttributes.value,
      });
      return JSON.parse(JSON.stringify(searchProducts));
    } catch (error) {
      console.error(error);
      Tracking.sentryCaptureError(error);
      return null;
    }
  }

  async function getTaxonomyMap() {
    try {
      const { productTaxonomyCategoryAttributeMap } = await executeQuery(TAXONOMY_MAP_QUERY);
      taxonomyMap.value = productTaxonomyCategoryAttributeMap;
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  async function getMoreCommonData(categoryId) {
    try {
      const { commonData } = await executeQuery(COMMON_CATEGORY_QUERY, {
        categoryId,
      });
      navCategories.value = JSON.parse(JSON.stringify(commonData.categories));
      maxClearanceDiscountCA.value = commonData.maxClearanceDiscountCA;
      maxClearanceDiscountUS.value = commonData.maxClearanceDiscountUS;
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  function clearFilters() {
    filters.taxonomyCategories = [];
    filters.taxonomyAttributes = [];
    filters.onClearance = undefined;
    filters.inStock = undefined;
    filters.hasReasonableETA = undefined;

    if (!isPromotionPage.value) {
      filters.onSale = undefined;
    }
  }

  async function getProductQuerySuggestions(suggestionQuery = '', suggestionCount = 8) {
    try {
      const { searchProductSuggestion } = await executeQuery(PRODUCT_SUGGESTION_QUERY, {
        suggestionQuery,
        suggestionCount,
      });

      return searchProductSuggestion;
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  async function searchProductsByIds(productIds = []) {
    try {
      const { searchProductsByIds } = await executeQuery(SEARCH_PRODUCTS_BY_IDS, {
        productIds,
      });
      if (searchProductsByIds && searchProductsByIds?.products?.length) {
        return searchProductsByIds.products;
      } else {
        Tracking.track(`Unable to search product info for product ids:${productIds.join(',')}`);
        return [];
      }
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  async function getProductSuggestions(query = '', count = 8) {
    try {
      const { searchProducts } = await executeQuery(SEARCH_PRODUCTS_QUERY, {
        query,
        page: 0,
        pageSize: count,
        countryCode: LocationService.getCurrentCountryCode(),
      });
      const result = JSON.parse(JSON.stringify(searchProducts));

      if (result.redirectUrl === null) result.redirectUrl = {};

      result.products.forEach((product) => {
        formatProductPrice(product);

        const variations = product.variation || product.variations;
        variations.forEach(formatProductPrice);
      });

      redirectUrl.value = result.redirectUrl;

      return result;
    } catch (error) {
      console.error(error);
      Tracking.sentryCaptureError(error);
    }
  }

  /**
   * Queue up a product click track event from a search page.
   *
   * @param {Number} productId The id of the product clicked
   * @param {String} queryId The query id from which the product was clicked
   * @param {Number} position The position/index in the search results that the product appeared
   */
  async function queueProductClick(productId, queryId, position) {
    try {
      if (productId && queryId && (position || position === 0)) {
        const positionAdjusted = position + 1;
        LocalStorage.set(
          LocalStorageKeys.SEARCH_PRODUCT_CLICK,
          JSON.stringify({
            queryId,
            productId,
            position: positionAdjusted,
            tracked: false,
          }),
        );
      } else {
        LocalStorage.set(LocalStorageKeys.SEARCH_PRODUCT_CLICK, null);
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Send a queued up product click event if the given product id matches the queued product id.
   *
   * @param {Number} productId The id of the product clicked
   */
  async function trackProductClick(productId) {
    try {
      const productClicked = JSON.parse(LocalStorage.get(LocalStorageKeys.SEARCH_PRODUCT_CLICK));
      if (
        productId &&
        productClicked &&
        productClicked.productId &&
        parseInt(productClicked.productId, 10) === productId &&
        !productClicked.tracked
      ) {
        await executeMutation(SEND_PRODUCT_SEARCH_RESULT_MUTATION, {
          input: {
            eventType: 'CLICK_PRODUCT',
            queryID: productClicked.queryId,
            objectID: productId.toString(),
            position: Number.parseInt(productClicked.position, 10),
          },
        });
        LocalStorage.set(LocalStorageKeys.SEARCH_PRODUCT_CLICK, JSON.stringify(Object.assign(productClicked, { tracked: true })));
      }
    } catch (e) {
      console.error(e);
    }
  }

  function setShouldForceSaleFilter(value) {
    shouldForceSaleFilter.value = value;
  }

  function setPreSelectedFilters(value) {
    preSelectedFilters.value = value;
  }

  function setPreSelectedTaxonomyAttributes(value) {
    preSelectedTaxonomyAttributes.value = value;
  }

  return {
    SORT_OPTIONS,
    products,
    maxPage,
    taxonomyCategories,
    taxonomyAttributes,
    filters,
    filterCount,
    clearFilters,
    productsCount,
    inStockEnabled,
    saleCount,
    clearanceCount,
    taxonomyMap,
    navCategories,
    isNoProducts,
    pending,
    seoPage,
    setHasReasonableETAFilter,
    searchProductsByIds,
    loadBrowseProducts,
    fetchProductsWithFilters,
    fetchProductsForContentfulProductFeed,
    formatProductPrice,
    getTaxonomyMap,
    getMoreCommonData,
    maxClearanceDiscountCA,
    maxClearanceDiscountUS,
    loadCurrentRouteFilters,
    isPromotionPage,
    queryAfterRemoval,
    queryID,
    getProductQuerySuggestions,
    getProductSuggestions,
    queueProductClick,
    trackProductClick,
    setShouldForceSaleFilter,
    setPreSelectedFilters,
    setPreSelectedTaxonomyAttributes,
    redirectUrl,
  };
}
